type InsurancePremiumParams = {
  maximumPurchasePrice: number
  mortgagePrinciple: number
  minimumDownPaymentRequired: number
}
const calculateInsurancePremium = (input: InsurancePremiumParams) => {
  const downPaymentRatio = input.minimumDownPaymentRequired / input.maximumPurchasePrice

  if (downPaymentRatio < 0.1) {
    return input.mortgagePrinciple * 0.04
  } else if (downPaymentRatio < 0.15) {
    return input.mortgagePrinciple * 0.031
  } else if (downPaymentRatio < 0.2) {
    return input.mortgagePrinciple * 0.028
  } else if (downPaymentRatio >= 0.2) {
    return input.mortgagePrinciple * 0
  }
}

const calculateMonthlyMortgagePayment = (
  mortgagePrinciple: number,
  insurancePremium: number,
  amortization: number,
  qualifyingRate?: number,
) => {
  const monthlyMortgagePayment =
    ((mortgagePrinciple + insurancePremium) * ((1 + qualifyingRate / 2) ** (1 / 6) - 1)) /
    (1 - ((1 + qualifyingRate / 2) ** (1 / 6)) ** -(amortization * 12))

  return monthlyMortgagePayment
}

const calculatePropertyRelatedExpenses = (maximumPurchasePrice: number) => {
  const heat = 100
  const propertyTax = (maximumPurchasePrice * 0.0075) / 12 // converting property taxes to monthly

  return heat + propertyTax
}

function calculateMonthlyHousingPayments(
  borrowerCreditRating: string,
  maximumPurchasePrice: number,
  downPayment: number,
  amortization: number,
  qualifyingRate: number,
) {
  const minimumDownPaymentRequired = calculateMinimumDownPaymentRequired(
    borrowerCreditRating,
    maximumPurchasePrice,
  )
  const mortgagePrinciple = maximumPurchasePrice - downPayment
  const insurancePremium = calculateInsurancePremium({
    maximumPurchasePrice,
    minimumDownPaymentRequired,
    mortgagePrinciple,
  })
  const monthlyMortgagePayment = calculateMonthlyMortgagePayment(
    mortgagePrinciple,
    insurancePremium,
    amortization,
    qualifyingRate,
  )
  const monthlyPropertyRelatedExpenses = calculatePropertyRelatedExpenses(maximumPurchasePrice)
  const monthHousingCosts = monthlyMortgagePayment + monthlyPropertyRelatedExpenses
  return monthHousingCosts
}

export const calculateMonthlyPersonalPayments = (
  monthlyLoanPayments: number,
  creditCardDebt: number,
) => {
  return monthlyLoanPayments + creditCardDebt * 0.03
}

export const calculateMinimumDownPaymentRequired = (
  borrowerCreditRating: string,
  maximumPurchasePrice: number,
) => {
  if (borrowerCreditRating === 'bad') {
    return maximumPurchasePrice * 0.2
  } else if (maximumPurchasePrice >= 1000000) {
    return maximumPurchasePrice * 0.2
  } else if (maximumPurchasePrice > 500000) {
    return 500000 * 0.05 + (maximumPurchasePrice - 500000) * 0.1
  } else if (maximumPurchasePrice <= 500000) {
    return maximumPurchasePrice * 0.05
  }
}

export const calculateGds = (
  borrowerCreditRating: string,
  maximumPurchasePrice: number,
  downPayment: number,
  annualIncome: number,
  amortization: number,
  qualifyingRate: number,
) => {
  const monthlyHousingPayments = calculateMonthlyHousingPayments(
    borrowerCreditRating,
    maximumPurchasePrice,
    downPayment,
    amortization,
    qualifyingRate,
  )
  const gds = monthlyHousingPayments / (annualIncome / 12) // converting annual income to monthly

  return gds
}

export const calculateTds = (
  borrowerCreditRating: string,
  maximumPurchasePrice: number,
  downPayment: number,
  annualIncome: number,
  monthlyPersonalPayments: number,
  amortization: number,
  qualifyingRate: number,
) => {
  const monthlyHousingPayments = calculateMonthlyHousingPayments(
    borrowerCreditRating,
    maximumPurchasePrice,
    downPayment,
    amortization,
    qualifyingRate,
  )
  const tds = (monthlyHousingPayments + monthlyPersonalPayments) / (annualIncome / 12)

  return tds
}

export const findMaxPriceBasedOnIncome = (input: {
  borrowerCreditRating: string
  downPayment: number
  annualIncome: number
  amortization: number
  qualifyingRate: number
}) => {
  if (input.annualIncome === 0 || input.downPayment === 0) {
    return 0
  }
  let maximumPurchasePrice = 500000
  let priceJump = 204800
  let counter = 0
  let lastPriceJumpNegative = null

  while (true) {
    const gds = calculateGds(
      input.borrowerCreditRating,
      maximumPurchasePrice,
      input.downPayment,
      input.annualIncome,
      input.amortization,
      input.qualifyingRate,
    )
    if (gds > 0.3895 && gds < 0.39) {
      break
    }

    if (gds <= 0.39) {
      if (lastPriceJumpNegative === true) {
        priceJump = priceJump / 2
      }
      maximumPurchasePrice += priceJump
      lastPriceJumpNegative = false
    } else {
      if (lastPriceJumpNegative === false) {
        priceJump = priceJump / 2
      }
      maximumPurchasePrice -= priceJump
      lastPriceJumpNegative = true
    }

    if (counter++ > 1000) {
      throw new Error('cannot_calculate_gds')
    }
  }

  return Math.max(maximumPurchasePrice, input.downPayment)
}

export const limitMaxPriceBasedOnDebts = (
  purchasePrice: number,
  input: {
    borrowerCreditRating: string
    annualIncome: number
    downPayment: number
    monthlyPersonalPayments: number
    amortization: number
    qualifyingRate: number
  },
) => {
  if (input.annualIncome === 0 || input.downPayment === 0) {
    return 0
  }

  let counter = 0

  while (true) {
    const tds = calculateTds(
      input.borrowerCreditRating,
      purchasePrice,
      input.downPayment,
      input.annualIncome,
      input.monthlyPersonalPayments,
      input.amortization,
      input.qualifyingRate,
    )

    if (tds < 0.44) {
      break
    }

    if (counter++ > 50000) {
      throw new Error('cannot_calculate_tds')
    }

    purchasePrice -= 1000
  }

  return Math.max(purchasePrice, input.downPayment)
}

export const limitMaxPriceBasedOnDownPayment = (
  purchasePrice: number,
  input: {
    borrowerCreditRating: string
    downPayment: number
  },
) => {
  if (input.downPayment === 0) {
    return 0
  }

  let counter = 0

  while (true) {
    const minimumRequired = calculateMinimumDownPaymentRequired(
      input.borrowerCreditRating,
      purchasePrice,
    )

    if (input.downPayment >= minimumRequired) {
      break
    }

    if (counter++ > 50000) {
      throw new Error('cannot_calculate_down_payment_limited_purchase_price')
    }

    purchasePrice -= 1000
  }

  return purchasePrice
}
